import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import ButtonMui from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';

export default function YesNoDialogButton({
  buttonContent,
  dialogTitle,
  children,
  positiveLabel = 'Yes',
  negativeLabel = 'No',
  buttonComponent = Button,
  positiveButtonComponent = Button,
  negativeButtonComponent = Button,
  onSubmit,
  ...props
}) {
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const handleClick = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const handleClose = useCallback(() => {
    if (!disabled) {
      setOpen(false);
      setDisabled(false);
    }
  }, [setOpen, setDisabled, disabled]);
  const content = typeof(children) === 'function'
    ? children()
    : children;
  const handleSubmit = useCallback(async () => {
    setDisabled(true);
    onSubmit(handleClose);
  }, [onSubmit]);
  const NegativeButtonComponent = negativeButtonComponent;
  const ButtonComponent = buttonComponent;

  return (
    <>
      <CustomDialog
        title={dialogTitle}
        {...{
          open, positiveLabel, negativeLabel, disabled,
          positiveButtonComponent, negativeButtonComponent,
        }}
        onClose={handleClose}
        onSubmit={handleSubmit}
      >
        {content}
        {disabled && <LinearProgress variant="query"/>}
      </CustomDialog>
      <ButtonComponent
        onClick={handleClick}
        disabled={open}
        {...props}
      >
        {buttonContent}
      </ButtonComponent>
    </>
  );
}

function CustomDialog({
  open, title, disabled, onClose, children,
  positiveLabel, negativeLabel = 'Cancel',
  positiveButtonComponent: PositiveButtonComponent,
  negativeButtonComponent: NegativeButtonComponent,
  onSubmit,
}) {
  return (
    <Dialog {...{ open, onClose }} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {children}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <NegativeButtonComponent
          {...{ disabled }}
          onClick={onClose}
        >
          {negativeLabel}
        </NegativeButtonComponent>
        <PositiveButtonComponent
          {...{ disabled }}
          color="primary"
          onClick={onSubmit}
        >
          {positiveLabel}
        </PositiveButtonComponent>
      </DialogActions>
    </Dialog>    
  );
}

export function YesNoDialog({
  open, title, onClose, children,
  positiveLabel, negativeLabel = 'Cancel',
  positiveButtonComponent = Button,
  negativeButtonComponent = Button,
  onSubmit,
}) {
  const [disabled, setDisabled] = useState(false);
  const handleClose = useCallback(() => {
    if (!disabled) {
      onClose();
      setDisabled(false);
    }
  }, [setDisabled, disabled]);
  const content = typeof(children) === 'function'
    ? children()
    : children;
  const handleSubmit = useCallback(async () => {
    setDisabled(true);
    if (await onSubmit(handleClose) === false) {
      return;
    }

    handleClose();
  }, [onSubmit]);

  return (
    <CustomDialog
      {...{
        open, title, positiveLabel, negativeLabel, disabled,
        positiveButtonComponent, negativeButtonComponent,
      }}
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      {content}
      {disabled && <LinearProgress variant="query"/>}
    </CustomDialog>
  );
}

export const Button = styled(ButtonMui)`
  text-transform: none !important;
  .MuiButton-label {
    font-weight: bold !important
    font-size: 16px !important;
  }
`;
